import React, { useEffect, useMemo, useRef } from "react";
import { FiAlertCircle, FiCheckCircle, FiInfo } from "react-icons/fi";
import { useOptionalToast } from "./ToastProvider";

type AlertTone = "error" | "warning" | "success" | "info";

export interface AlertProps {
  tone?: AlertTone;
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  /**
   * Espelhar em toast (só vale para `error` e `success`; o padrão é sim).
   *
   * Passe `false` quando o aviso já é permanente na tela e o estado que o gera
   * pode oscilar. O toast de erro é persistente — não some sozinho —, então
   * cada volta do estado traz de novo um aviso que a pessoa já fechou, dizendo
   * o que ela já está lendo na tela. Foi o caso do erro de conexão do WhatsApp,
   * que aparece no chip do cabeçalho e no cartão de conexão enquanto o worker
   * tenta reconectar.
   */
  mirrorToast?: boolean;
}

const toneClass: Record<AlertTone, string> = {
  error: "border-rose-200/80 bg-rose-50/80 text-rose-800",
  warning: "border-amber-200/80 bg-amber-50/80 text-amber-800",
  success: "border-emerald-200/80 bg-emerald-50/80 text-emerald-800",
  info: "border-sky-200/80 bg-sky-50/80 text-sky-800",
};

const defaultIcon: Record<AlertTone, React.ReactNode> = {
  error: <FiAlertCircle />,
  warning: <FiAlertCircle />,
  success: <FiCheckCircle />,
  info: <FiInfo />,
};

function getAlertText(children: React.ReactNode): string | null {
  return (
    React.Children.toArray(children)
      .map((child) => {
        if (typeof child === "string" || typeof child === "number") return String(child);
        if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
          return getAlertText(child.props.children) || "";
        }
        return "";
      })
      .join("")
      .trim() || null
  );
}

export function Alert({
  tone = "info",
  children,
  icon,
  className = "",
  mirrorToast = true,
}: AlertProps) {
  const toast = useOptionalToast();
  const toastId = useRef<string | null>(null);
  const message = useMemo(() => getAlertText(children), [children]);

  useEffect(() => {
    if (!mirrorToast || !toast || !message || (tone !== "error" && tone !== "success")) {
      return;
    }

    toastId.current = toast.show(message, tone, {
      persistent: tone === "error",
    });

    return () => {
      if (toastId.current) toast.dismiss(toastId.current);
      toastId.current = null;
    };
  }, [message, toast, tone, mirrorToast]);

  return (
    <div
      className={`rounded-xl border px-4 py-3 text-sm font-semibold shadow-sm ${toneClass[tone]} ${className}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0 text-lg" aria-hidden="true">
          {icon || defaultIcon[tone]}
        </span>
        <div className="min-w-0 leading-6">{children}</div>
      </div>
    </div>
  );
}
